/**
 * What to suggest a person asks, derived from what they can actually do.
 *
 * A starter prompt is a promise. "Compare the Northside and Riverview programs"
 * offered to somebody who holds neither program's data is a screen setting a
 * person up to be refused by its own server — and it is worse than useless,
 * because being refused by an assistant reads as the assistant being broken
 * rather than as the account being scoped. So nothing here is a fixed list.
 *
 * THE AUTHORITY IS THE SERVER'S OWN ANSWER, not a client-side re-derivation.
 * `GET /api/intelligence/tools` returns the tools resolved from THIS caller's
 * real permissions (`advertiseToolSpecs` → `registry.listForCaller(can)`), so a
 * suggestion is offered only when a tool that could answer it was advertised.
 * That makes the gate the same computation the executor will run, rather than a
 * second guess at it that can drift. Permission keys appear below only where a
 * suggestion needs to choose BETWEEN two honest phrasings of the same tool —
 * `supervision.status` returns the whole organization to a manager and only the
 * caller's own row to everyone else, so the sentence has to know which.
 *
 * THREE RULES, in the order they bite:
 *
 *   1. NOT KNOWN YET IS NOT ZERO. While the tool list is in flight, or if it
 *      failed, this returns nothing at all. A generic fallback list would be
 *      the one moment the screen invents authority it has not read.
 *   2. FEWER, NEVER FILLER. A caller whose authority supports two honest
 *      suggestions sees two. The catalog is never padded to a target count.
 *   3. NAMES COME FROM READS. A suggestion naming a program names one the
 *      caller's own `program:read` actually returned; with no programs read,
 *      the program suggestions simply do not appear.
 */

/** One offered prompt. `text` is what the person sees AND what gets asked. */
export interface Suggestion {
  /** Stable key for rendering and for tests to name a specific suggestion. */
  id: string;
  text: string;
}

/** Everything the derivation is allowed to look at. */
export interface SuggestionInput {
  /**
   * Tool ids the SERVER advertised for this caller. `undefined` means the list
   * is not known yet (in flight, refused or failed) — which yields no
   * suggestions, never a default set.
   */
  advertisedToolIds: readonly string[] | undefined;
  /**
   * The scope-aware permission mirror (`usePermissions().can`), default-deny.
   * Used ONLY to pick between phrasings, never to open a suggestion a tool did
   * not already open.
   */
  can: (key: string) => boolean;
  /**
   * Active program names the caller's own `program:read` returned, already
   * narrowed by the server. `undefined` means not read (unheld, in flight or
   * failed); an empty array means read and genuinely none.
   */
  programNames: readonly string[] | undefined;
}

/**
 * The cap. Four is a row on a laptop and two rows on a phone; beyond that the
 * empty state stops being an invitation and becomes a menu to read.
 */
export const MAX_SUGGESTIONS = 4;

interface Candidate {
  id: string;
  /** Any ONE of these being advertised makes the suggestion honest. */
  anyTool: readonly string[];
  /** The sentence, or null when it cannot be phrased truthfully for this caller. */
  build: (input: SuggestionInput) => string | null;
}

/**
 * The catalog, in priority order — the order a person reads them in, and the
 * order they are dropped in when a caller's authority supports more than four.
 * Organization-shaped questions lead for a reader who has organization-shaped
 * reach; the self-scoped variants sit below them and are the only ones a
 * narrowly-scoped account ever reaches.
 */
const CANDIDATES: readonly Candidate[] = [
  {
    // supervision.status/overview scope themselves: `supervision:manage` sees
    // the organization, everyone else sees their own row (retrievalTools.ts).
    // This is the manager's arm of that fork.
    id: 'supervision-overdue',
    anyTool: ['supervision.overview', 'supervision.status'],
    build: ({ can }) => (can('supervision:manage') ? 'Is anyone overdue for supervision?' : null),
  },
  {
    id: 'program-compare',
    anyTool: ['analytics.query_metric'],
    build: ({ programNames }) => {
      if (programNames === undefined || programNames.length < 2) return null;
      return `How do hours worked compare between ${programNames[0]} and ${programNames[1]} this month?`;
    },
  },
  {
    id: 'roster-active',
    anyTool: ['workforce.list_workers'],
    build: () => 'Which workers are active right now?',
  },
  {
    id: 'program-hours',
    anyTool: ['analytics.query_metric'],
    build: ({ programNames }) => {
      if (programNames === undefined || programNames.length !== 1) return null;
      return `How many hours did ${programNames[0]} deliver this month?`;
    },
  },
  {
    // Advertised only to a `dashboard:manage` holder — the tool's own
    // permission group is the gate, so no extra key check is needed here.
    id: 'dashboard-build',
    anyTool: ['dashboard.create'],
    build: () => 'Build me a dashboard of this month’s hours by program.',
  },
  {
    id: 'automation-alert',
    anyTool: ['automation.plan', 'automation.create'],
    build: ({ can }) => {
      // The three tiers the automation tools gate on, strongest first. Naming
      // the wrong tier would offer reach the caller does not have, so an
      // account holding none of the three gets no sentence at all.
      if (can('rules:configure')) {
        return 'Set up an organization-wide alert when a metric crosses a threshold.';
      }
      if (can('automation:manage_team')) {
        return 'Set up an alert for my team when a metric crosses a threshold.';
      }
      if (can('automation:manage_own')) {
        return 'Set up an alert just for me when a metric crosses a threshold.';
      }
      return null;
    },
  },
  {
    id: 'metrics-catalog',
    anyTool: ['dashboard.list_metrics'],
    build: () => 'What can you measure for this organization?',
  },
  {
    id: 'connectors-health',
    anyTool: ['connectors.health'],
    build: () => 'Are any of our integrations running degraded?',
  },
  {
    id: 'knowledge-policies',
    anyTool: ['knowledge.list'],
    build: () => 'Which policies and guidance can I read?',
  },
  {
    // The self-scoped arm of the supervision fork: what `supervision.status`
    // returns to somebody without `supervision:manage` is their own row, so
    // this is the only honest sentence for them.
    id: 'supervision-mine',
    anyTool: ['supervision.status'],
    build: ({ can }) => (can('supervision:manage') ? null : 'Am I current on my supervision?'),
  },
];

/**
 * The suggestions this caller may honestly be offered, at most
 * {@link MAX_SUGGESTIONS}, in catalog order. An empty array is a legitimate
 * answer and the screen renders it as one — no starters, not a generic list.
 */
export function deriveSuggestions(input: SuggestionInput): Suggestion[] {
  const advertised = input.advertisedToolIds;
  if (advertised === undefined) return [];
  const open = new Set(advertised);
  const out: Suggestion[] = [];
  for (const candidate of CANDIDATES) {
    if (out.length >= MAX_SUGGESTIONS) break;
    if (!candidate.anyTool.some((toolId) => open.has(toolId))) continue;
    const text = candidate.build(input);
    if (text === null) continue;
    out.push({ id: candidate.id, text });
  }
  return out;
}

/**
 * The signed-in person's first name, or `undefined` when there is nothing
 * honest to use.
 *
 * `/auth/me` carries `name` — one IdP-owned display string — and no first-name
 * field, so this is the house split (`SignIn.tsx`): trim, break on whitespace,
 * take the first token. An email address is deliberately NOT accepted as a
 * fallback: "Good evening, a.mohamed" is worse than "Good evening."
 */
export function firstNameOf(name: string | null | undefined): string | undefined {
  if (typeof name !== 'string') return undefined;
  const first = name.trim().split(/\s+/).filter(Boolean)[0];
  if (first === undefined || first === '') return undefined;
  return first;
}

/** The three greetings, on the same boundaries `HeroGreeting` already uses. */
export type GreetingKey = 'greeting.morning' | 'greeting.afternoon' | 'greeting.evening';

export function greetingKeyFor(hour: number): GreetingKey {
  if (hour < 12) return 'greeting.morning';
  if (hour < 18) return 'greeting.afternoon';
  return 'greeting.evening';
}
